import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Grid,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { styled } from '@mui/system';
import api from '../config/api';
import useStore from "../config/store";
import { useTranslation } from 'react-i18next';

// Componente para mostrar información del candidato seleccionado
const CandidateInfo = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.primary.light + '10',
  border: `1px solid ${theme.palette.primary.light}`,
  borderRadius: theme.shape.borderRadius,
}));

const CandidateSearchForm = ({ 
  authToken, 
  onCandidateChange, 
  initialName = "", 
  initialEmail = "", 
  initialApplicant = null 
}) => {
  // Estados para la búsqueda de candidatos
  const [allApplicants, setAllApplicants] = useState([]);
  const [candidateName, setCandidateName] = useState(initialName);
  const [candidateEmail, setCandidateEmail] = useState(initialEmail);
  const [selectedApplicant, setSelectedApplicant] = useState(initialApplicant);
  const [nameOptions, setNameOptions] = useState([]);
  const [emailOptions, setEmailOptions] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isSelectingFromDropdown, setIsSelectingFromDropdown] = useState(false);

  const { project } = useStore.getState();
  const { t } = useTranslation();

  // Función para validar email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Notificar cambios al componente padre
  const notifyChange = useCallback((name, email, applicant) => {
    onCandidateChange({
      name: name || "",
      email: email || "",
      applicant: applicant
    });
  }, [onCandidateChange]);

  // Cargar todos los applicants al inicio
  const loadAllApplicants = async () => {
    if (!authToken) return;
    
    try {
      setLoadingApplicants(true);
      const { data } = await api.get(`/applicants?projectId=${project}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      if (data.response && data.response.status_response === 'OK') {
        setAllApplicants(data.response.data || []);
      }
    } catch (error) {
      console.error("Error al cargar applicants:", error);
    } finally {
      setLoadingApplicants(false);
    }
  };

  // Filtrar applicants por nombre
  const filterApplicantsByName = useCallback((inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      setNameOptions([]);
      return;
    }

    const filtered = allApplicants.filter(applicant => 
      applicant.name?.toLowerCase().includes(inputValue.toLowerCase())
    );

    setNameOptions(filtered);
  }, [allApplicants]);

  // Filtrar applicants por email
  const filterApplicantsByEmail = useCallback((inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmailOptions([]);
      return;
    }

    const filtered = allApplicants.filter(applicant => {
      if (!applicant.email) return false;
      return applicant.email.toLowerCase().includes(inputValue.toLowerCase());
    });

    setEmailOptions(filtered);
  }, [allApplicants]);

  // Seleccionar applicant completo
  const selectApplicant = (applicant) => {
    setIsSelectingFromDropdown(true);
    setSelectedApplicant(applicant);
    setCandidateName(applicant.name);
    setCandidateEmail(applicant.email);
    setEmailError("");
    setNameOptions([]);
    setEmailOptions([]);
    notifyChange(applicant.name, applicant.email, applicant);
    
    // Limpiar el flag después de un breve delay
    setTimeout(() => {
      setIsSelectingFromDropdown(false);
    }, 200);
  };

  // Limpiar selección
  const clearSelection = () => {
    setSelectedApplicant(null);
    setNameOptions([]);
    setEmailOptions([]);
  };

  // Manejar cambio en el nombre
  const handleNameChange = (event, newValue) => {
    if (typeof newValue === 'object' && newValue !== null) {
      // Se seleccionó un applicant
      selectApplicant(newValue);
    }
  };

  const handleNameInputChange = (event, newInputValue) => {
    if (isSelectingFromDropdown) {
      return;
    }
    
    setCandidateName(newInputValue);
    
    if (!newInputValue) {
      clearSelection();
    } else if (!selectedApplicant || selectedApplicant.name !== newInputValue) {
      setSelectedApplicant(null);
      filterApplicantsByName(newInputValue);
    }
    
    notifyChange(newInputValue, candidateEmail, selectedApplicant);
  };

  // Manejar cambio en el email
  const handleEmailChange = (event, newValue) => {
    if (typeof newValue === 'object' && newValue !== null) {
      // Se seleccionó un applicant
      selectApplicant(newValue);
    }
  };

  const handleEmailInputChange = (event, newInputValue) => {
    if (isSelectingFromDropdown) {
      return;
    }
    
    setCandidateEmail(newInputValue);
    
    // Validar email
    if (newInputValue && newInputValue.includes('@')) {
      if (isValidEmail(newInputValue)) {
        setEmailError("");
      } else {
        setEmailError("Email no válido");
      }
    } else {
      setEmailError("");
    }
    
    if (!newInputValue) {
      clearSelection();
    } else if (!selectedApplicant || selectedApplicant.email !== newInputValue) {
      setSelectedApplicant(null);
      filterApplicantsByEmail(newInputValue);
    }
    
    notifyChange(candidateName, newInputValue, selectedApplicant);
  };

  // Cargar applicants al montar el componente
  useEffect(() => {
    loadAllApplicants();
  }, [authToken]);

  // Sincronizar campos cuando se selecciona un applicant
  useEffect(() => {
    if (selectedApplicant && !isSelectingFromDropdown) {
      setCandidateName(selectedApplicant.name || "");
      setCandidateEmail(selectedApplicant.email || "");
    }
  }, [selectedApplicant]);

  // Sincronizar valores iniciales (optimizado para evitar re-renders excesivos)
  useEffect(() => {
    // Solo actualizar si los valores realmente han cambiado
    if (candidateName !== initialName || 
        candidateEmail !== initialEmail || 
        selectedApplicant !== initialApplicant) {
      
      setCandidateName(initialName || "");
      setCandidateEmail(initialEmail || "");
      setSelectedApplicant(initialApplicant);
      
      // Si hay applicant inicial, asegurar que los campos estén sincronizados
      if (initialApplicant) {
        setCandidateName(initialApplicant.name || "");
        setCandidateEmail(initialApplicant.email || "");
      }
    }
  }, [initialName, initialEmail, initialApplicant]);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <PersonIcon sx={{ mr: 1 }} />
        {t('reports.applicant.applicantData')}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            value={selectedApplicant}
            inputValue={candidateName}
            options={nameOptions}
            getOptionLabel={(option) => {
              if (typeof option === 'object') return option.name || '';
              return option;
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {option.rut} | {t('email')}: {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
            onInputChange={handleNameInputChange}
            onChange={handleNameChange}
            loading={loadingApplicants}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('fullName')}
                placeholder={t('reports.applicant.applicantNamePlaceholder')}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingApplicants && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={t('reports.applicant.applicantNoMatch')}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            value={selectedApplicant}
            inputValue={candidateEmail}
            options={emailOptions}
            getOptionLabel={(option) => {
              if (typeof option === 'object') return option.email || '';
              return option;
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {option.rut} | {t('email')}: {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
            onInputChange={handleEmailInputChange}
            onChange={handleEmailChange}
            loading={loadingApplicants}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('email')}
                placeholder={t('reports.applicant.applicantNoMatch')}
                fullWidth
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingApplicants && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={t('reports.applicant.applicantNoMatch')}
          />
        </Grid>
      </Grid>

      {/* Mostrar información del candidato seleccionado */}
      {selectedApplicant && (
        <CandidateInfo>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PersonIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle2" color="primary">
              {t('reports.applicant.selectedApplicant')}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2">
                <strong>{t('name')}:</strong> {selectedApplicant.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2">
                <strong>{t('email')}:</strong> {selectedApplicant.email}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2">
                <strong>{t('uniqueIdentifier')}:</strong> {selectedApplicant.rut}
              </Typography>
            </Grid>
            {selectedApplicant.phone && (
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>{t('phone')}:</strong> {selectedApplicant.phone}
                </Typography>
                </Grid>
            )}
          </Grid>
        </CandidateInfo>
      )}
    </Box>
  );
};

export default CandidateSearchForm;